import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { useI18n } from "@/lib/i18n";

const lineReveal = {
  initial: { y: "110%" },
  animate: (i) => ({
    y: "0%",
    transition: { delay: 0.2 + i * 0.12, duration: 1.05, ease: [0.76, 0, 0.24, 1] },
  }),
};

export default function Hero() {
  const { t } = useI18n();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [0.55, 0]);

  return (
    <section
      ref={ref}
      className="relative min-h-[100svh] w-full overflow-hidden bg-[#050505]"
      data-testid="hero-section"
    >
      {/* Parallax background */}
      <motion.div
        style={{ y, scale }}
        className="absolute inset-0 will-change-transform"
        aria-hidden
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.pexels.com/photos/257636/pexels-photo-257636.jpeg')",
            filter: "grayscale(0.6) contrast(1.1) brightness(0.5)",
          }}
        />
        <motion.div
          style={{ opacity }}
          className="absolute inset-0 bg-gradient-to-b from-[#050505]/50 via-[#050505]/40 to-[#050505]"
        />
      </motion.div>

      {/* Radial vignette */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background: "radial-gradient(ellipse at 30% 40%, transparent 0%, #050505 90%)"
      }} />

      {/* Content */}
      <div className="relative z-10 mx-auto max-w-[1440px] px-6 md:px-10 pt-32 md:pt-40 pb-20 min-h-[100svh] flex flex-col justify-between">
        {/* Top badge */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="flex items-center gap-3 font-mono-acc text-[11px] tracking-[0.25em] uppercase text-[#A1A1AA]"
          data-testid="hero-badge"
        >
          <span className="w-8 h-px bg-[#E1FF00]" />
          {t.hero.badge}
        </motion.div>

        {/* Kinetic headline */}
        <div className="mt-16 md:mt-24">
          <h1 className="font-display font-semibold text-[#F4F4F5] leading-[0.9] tracking-tighter uppercase text-[14vw] md:text-[13vw] lg:text-[11.5rem]">
            {[t.hero.line1, t.hero.line2, t.hero.line3].map((line, i) => (
              <span key={i} className="reveal-mask">
                <motion.span
                  className="reveal-inner"
                  variants={lineReveal}
                  custom={i}
                  initial="initial"
                  animate="animate"
                >
                  {i === 2 ? <span className="text-[#E1FF00]">{line}</span> : line}
                </motion.span>
              </span>
            ))}
          </h1>
        </div>

        {/* Bottom row */}
        <div className="mt-16 md:mt-20 grid md:grid-cols-12 gap-8 items-end">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.7 }}
            className="md:col-span-6 lg:col-span-5 text-[#A1A1AA] text-base md:text-lg max-w-md"
          >
            {t.hero.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.4, duration: 0.7 }}
            className="md:col-span-3 lg:col-span-4 flex flex-col sm:flex-row gap-3"
          >
            <a
              href="#quote"
              className="group inline-flex items-center justify-between gap-3 bg-[#E1FF00] hover:bg-[#CBE600] text-[#050505] px-5 py-4 font-mono-acc text-[11px] tracking-[0.15em] uppercase font-semibold transition-colors"
              data-testid="hero-cta-quote"
            >
              {t.hero.cta}
              <span className="inline-block group-hover:translate-x-1 transition-transform">→</span>
            </a>
            <a
              href="#services"
              className="inline-flex items-center justify-between gap-3 border border-[#27272A] hover:border-[#E1FF00] text-[#F4F4F5] px-5 py-4 font-mono-acc text-[11px] tracking-[0.15em] uppercase transition-colors"
              data-testid="hero-cta-services"
            >
              {t.hero.secondary}
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.55, duration: 0.7 }}
            className="md:col-span-3 lg:col-span-3 grid grid-cols-3 md:grid-cols-1 gap-4 md:border-l md:border-[#27272A] md:pl-6"
          >
            {[
              [t.hero.stat1, t.hero.stat1Label],
              [t.hero.stat2, t.hero.stat2Label],
              [t.hero.stat3, t.hero.stat3Label],
            ].map(([val, label], i) => (
              <div key={i}>
                <div className="font-display text-2xl md:text-3xl text-[#F4F4F5] leading-none">{val}</div>
                <div className="mt-1 font-mono-acc text-[10px] tracking-widest uppercase text-[#A1A1AA]">{label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
